"use client";

// @subframe/sync-disable

/*
 * Documentation:
 * ASButton_Left — https://app.subframe.com/feb895689ba0/library?component=ASButton_Left_3e87acd0-92f5-4982-8de2-f13b3c705ce2
 * Loader — https://app.subframe.com/feb895689ba0/library?component=Loader_f2e570c8-e463-45c2-aae9-a960146bc5d5
 * card info — https://app.subframe.com/feb895689ba0/library?component=card+info_d56da580-8331-4257-905f-6ffb320343ad
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import { Loader } from "./Loader";
import { CardInfo } from "./CardInfo";

interface AsButtonLeftRootProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | "default"
    | "secondary"
    | "brand-ghost"
    | "brand"
    | "destructive-secondary"
    | "destructive-ghost"
    | "neutral-secondary"
    | "ghost"
    | "link";
  leadingIcon?: SubframeCore.IconName;
  trailingIcon?: SubframeCore.IconName;
  text?: React.ReactNode;
  size?: "default" | "md" | "lg" | "sm" | "xs";
  loader?: boolean;
  disabled?: boolean;
  disabledSecondary?: boolean;
  cardInfo?: boolean;
  shadow?: boolean;
  className?: string;
  children?: React.ReactNode|any;
}

const AsButtonLeftRoot = React.forwardRef<HTMLElement, AsButtonLeftRootProps>(
  function AsButtonLeftRoot(
    {
      variant = "default",
      leadingIcon = null,
      trailingIcon = null,
      text,
      size = "default",
      loader = false,
      disabled = false,
      disabledSecondary = false,
      cardInfo = false,
      shadow = false,
      className,
      children,
      ...otherProps
    }: AsButtonLeftRootProps,
    ref
  ) {
    return (
      <button
        className={SubframeCore.twClassNames(
          "group/3e87acd0 flex h-10 cursor-pointer items-center gap-2 rounded-md border border-solid border-neutral-900 bg-neutral-900 px-3 py-2 hover:border hover:border-solid hover:border-neutral-700 hover:bg-neutral-700 active:border active:border-solid active:border-black active:bg-black",
          {
            "shadow-lg": shadow,
            "border border-solid border-neutral-400 bg-neutral-200 hover:border hover:border-solid hover:border-neutral-400 hover:bg-neutral-200 active:border active:border-solid active:border-neutral-400 active:bg-neutral-200":
              disabledSecondary,
            "border-none bg-neutral-200 hover:border-none hover:bg-neutral-200 active:border-none active:bg-neutral-200":
              disabled,
            "h-7 w-auto px-1 py-2": size === "xs",
            "h-8 w-auto": size === "sm",
            "h-14 w-auto": size === "lg",
            "h-12 w-auto": size === "md",
            "rounded-none border-none bg-transparent hover:border-none hover:bg-transparent active:border-none active:bg-transparent":
              variant === "link",
            "rounded-md border-none bg-transparent px-2 py-2 hover:rounded-md hover:border-none hover:bg-neutral-100 active:border-none active:bg-neutral-200 focus-within:bg-neutral-100":
              variant === "ghost",
            "border border-solid border-neutral-100 bg-white hover:border hover:border-solid hover:border-neutral-100 hover:bg-neutral-50 active:border active:border-solid active:border-neutral-100 active:bg-white focus-within:border-none focus-within:bg-neutral-100":
              variant === "neutral-secondary",
            "border-none bg-transparent hover:border-none hover:bg-error-100 active:border-none active:bg-error-200 focus-within:bg-error-100":
              variant === "destructive-ghost",
            "border-none bg-error-50 hover:border-none hover:bg-error-200 active:border-none active:bg-error-50":
              variant === "destructive-secondary",
            "border border-solid border-brand-500 bg-brand-500 hover:border hover:border-solid hover:border-brand-600 hover:bg-brand-200 active:border active:border-solid active:border-brand-500 active:bg-brand-500":
              variant === "brand",
            "rounded-md border-none bg-transparent hover:border-none hover:bg-brand-100 active:border-none active:bg-brand-300 focus-within:bg-brand-300":
              variant === "brand-ghost",
            "bg-white hover:border hover:border-solid hover:border-brand-900 hover:bg-brand-100 active:bg-brand-300 focus-within:bg-brand-300":
              variant === "secondary",
          },
          className
        )}
        ref={ref as any}
        {...otherProps}
      >
        <SubframeCore.Icon
          className={SubframeCore.twClassNames(
            "text-body font-body text-white",
            {
              hidden: loader,
              "text-neutral-900": variant === "ghost",
              "text-neutral-900 group-active/3e87acd0:text-brand-900":
                variant === "brand",
              "text-neutral-900 group-hover/3e87acd0:text-brand-900 group-active/3e87acd0:text-neutral-900":
                variant === "secondary",
            }
          )}
          name={leadingIcon}
        />
        <div
          className={SubframeCore.twClassNames(
            "hidden items-center justify-center gap-2",
            { flex: loader, "group-hover/3e87acd0:hidden": variant === "ghost" }
          )}
        >
          <Loader size="medium" />
        </div>
        {text ? (
          <span
            className={SubframeCore.twClassNames(
              "text-body-bold font-body-bold text-white",
              {
                "text-neutral-400": disabledSecondary || disabled,
                hidden: loader,
                "font-['Inter'] text-[14px] font-[500] leading-[20px] tracking-normal":
                  size === "xs",
                "text-neutral-700 group-hover/3e87acd0:text-neutral-900":
                  variant === "link",
                "text-neutral-900":
                  variant === "ghost" || variant === "neutral-secondary",
                "text-error-700":
                  variant === "destructive-ghost" ||
                  variant === "destructive-secondary",
                "text-neutral-900 group-active/3e87acd0:text-brand-900":
                  variant === "brand",
                "text-neutral-900 group-hover/3e87acd0:text-brand-900":
                  variant === "brand-ghost" || variant === "secondary",
              }
            )}
          >
            {text}
          </span>
        ) : null}
        <CardInfo
          className={SubframeCore.twClassNames("hidden", { flex: cardInfo })}
          image="https://res.cloudinary.com/subframe/image/upload/v1726598383/uploads/3988/dvl5twcraiwmn914brec.png"
          text="9999"
          variant="inverse"
        />
        <SubframeCore.Icon
          className={SubframeCore.twClassNames(
            "text-body font-body text-white group-hover/3e87acd0:text-white",
            {
              "text-neutral-900":
                variant === "ghost" || variant === "brand-ghost",
              "text-neutral-900 group-hover/3e87acd0:text-neutral-900":
                variant === "brand",
              "text-neutral-900 group-hover/3e87acd0:text-brand-900 group-active/3e87acd0:text-neutral-900":
                variant === "secondary",
            }
          )}
          name={trailingIcon}
        />
        {children}
      </button>
    );
  }
);

export const AsButtonLeft = AsButtonLeftRoot;
